/**
 *	One pass, left to right: the rules of a grammar are compiled into a single
 *	alternation, so at every position the highest-priority rule that matches
 *	wins and the match is consumed whole.
 *
 *	That is the difference from the kitchen sink's norrsken, which ran each
 *	pattern over the output of the last one and so could colour a keyword
 *	inside an already-coloured string, or a token inside the markup it had just
 *	written.
 */
import {
	type Grammar,
	type TokenName,
	grammarFor,
} from '@shared/docs/norrsken/grammars';

export interface Token {
	value: string;

	/** Absent for the text between matches. */
	name?: TokenName;
}

/**
 *	Compiled grammars, keyed by the grammar itself — each is a module constant,
 *	so the map holds one entry per language for the life of the process.
 */
const compiled: WeakMap<Grammar, RegExp> = new WeakMap();

/**
 *	Named groups carry the token name out of the match, which is what lets a
 *	rule's own groups stay non-capturing without index bookkeeping.
 */
function compile(grammar: Grammar): RegExp {
	const existing = compiled.get(grammar);

	if (existing) {
		return existing;
	}

	const pattern = new RegExp(
		grammar.map(([name, rule]) => `(?<${name}>${rule})`).join('|'),
		'gm'
	);

	compiled.set(grammar, pattern);

	return pattern;
}

function nameOf(
	groups: Optional<Record<string, string | undefined>>
): Optional<TokenName> {
	const matched = Object.entries(groups ?? {}).find(
		([, value]) => value !== undefined
	);

	return matched?.[0] as Optional<TokenName>;
}

/**
 *	Splits source into coloured and plain runs. An unknown language yields one
 *	plain run, which renders exactly as an unhighlighted block would.
 */
export function tokenize(source: string, language: Optional<string>): Token[] {
	const grammar = grammarFor(language);

	if (!grammar) {
		return [{ value: source }];
	}

	const tokens: Token[] = [];
	let consumed = 0;

	for (const match of source.matchAll(compile(grammar))) {
		const name = nameOf(match.groups);

		if (!name || match.index === undefined) {
			continue;
		}

		if (match.index > consumed) {
			tokens.push({ value: source.slice(consumed, match.index) });
		}

		tokens.push({ name, value: match[0] });
		consumed = match.index + match[0].length;
	}

	if (consumed < source.length) {
		tokens.push({ value: source.slice(consumed) });
	}

	return tokens;
}

/**
 *	The same tokens, cut into one list per line.
 *
 *	A token can span lines — a block comment, a raw string — so the split
 *	happens inside tokens rather than between them, with each piece keeping the
 *	name the whole match was given. A line is a row in the rendered block, which
 *	is what gives it a number and lets it wrap on its own.
 */
export function toLines(tokens: Token[]): Token[][] {
	const lines: Token[][] = [[]];

	for (const token of tokens) {
		const pieces = token.value.split('\n');

		pieces.forEach((piece, index) => {
			if (index > 0) {
				lines.push([]);
			}

			if (piece !== '') {
				lines.at(-1)?.push({ ...token, value: piece });
			}
		});
	}

	// A block's source ends in a newline, which is a line break rather than an
	// empty last line.
	if (lines.length > 1 && lines.at(-1)?.length === 0) {
		lines.pop();
	}

	return lines;
}
